export interface AgencyHeaderSummary {
  title: string;
  subtitle: string;
  badge: string;
}

export interface AgencyHealthBreakdownItem {
  label: string;
  value: number;
  percentage: number;
}

export interface AgencyKpiCard {
  label: string;
  value: string | number;
  helperText?: string;
}

export interface AgencyTableRow {
  customer: string;
  sites: number;
  avgSeoScore: number;
  criticalSites: number;
  criticalIssues: number;
  openIssues: number;
  organicTraffic: string;
  trafficChange: string;
  top10Keywords: number;
  lastActivity: string;
  status: string;
}

export interface AgencyTrendItem {
  label: string;
  value: string;
  deltaLabel: string;
}

export interface AgencyWebsiteTableRow {
  customer: string;
  website: string;
  seoScore: number;
  health: string;
  criticalIssues: number;
  openIssues: number;
  organicTraffic: string;
  trafficChange: string;
  rankings: string;
  lastCrawl: string;
  accountManager: string;
  status: string;
}

export interface AgencyTrendPoint {
  name: string;
  score: number;
  traffic: number;
  issues: number;
}

export interface AgencyMetricSnapshotLike {
  organicTraffic?: string | number;
  trafficChange?: string | number;
  gscClicks?: string | number;
  gscImpressions?: string | number;
  ctr?: string | number;
  top10Keywords?: number;
}

export interface AgencyHeaderSummaryInput {
  agencyName?: string;
  customerCount: number;
  siteCount: number;
}

export interface AgencyHealthBreakdownInput {
  healthy: number;
  good: number;
  issues: number;
  critical: number;
}

export interface AgencyKpiCardInput {
  customerCount: number;
  siteCount: number;
  criticalSites: number;
  openIssues: number;
  averageHealthScore: number;
  gscClicks: string | number;
  gscImpressions: string | number;
  ctr: string | number;
}

export interface AgencyDateRange {
  start?: string;
  end?: string;
}

export interface AgencyFilterState {
  customer?: string;
  site?: string;
  search?: string;
  health?: string;
  status?: string;
  dateRange?: AgencyDateRange;
}

export const buildAgencyHeaderSummary = ({ agencyName = 'Agency', customerCount, siteCount }: AgencyHeaderSummaryInput): AgencyHeaderSummary => ({
  title: `${agencyName} portfolio`,
  subtitle: `${customerCount} customers • ${siteCount} sites • portfolio health overview`,
  badge: 'Portfolio overview',
});

export const buildAgencyHealthBreakdown = ({ healthy, good, issues, critical }: AgencyHealthBreakdownInput): AgencyHealthBreakdownItem[] => {
  const total = healthy + good + issues + critical;
  if (total === 0) {
    return [
      { label: 'Healthy', value: 0, percentage: 0 },
      { label: 'Good', value: 0, percentage: 0 },
      { label: 'Needs Attention', value: 0, percentage: 0 },
      { label: 'Critical', value: 0, percentage: 0 },
    ];
  }

  const toPercentage = (value: number) => Math.round((value / total) * 100);

  return [
    { label: 'Healthy', value: healthy, percentage: toPercentage(healthy) },
    { label: 'Good', value: good, percentage: toPercentage(good) },
    { label: 'Needs Attention', value: issues, percentage: toPercentage(issues) },
    { label: 'Critical', value: critical, percentage: toPercentage(critical) },
  ];
};

export const buildAgencyKpiCards = ({
  customerCount,
  siteCount,
  criticalSites,
  openIssues,
  averageHealthScore,
  gscClicks,
  gscImpressions,
  ctr,
}: AgencyKpiCardInput): AgencyKpiCard[] => [
  { label: 'Customers', value: customerCount, helperText: 'Managed accounts' },
  { label: 'Websites', value: siteCount, helperText: 'Tracked properties' },
  { label: 'Critical sites', value: criticalSites, helperText: 'Need immediate action' },
  { label: 'Open issues', value: openIssues, helperText: 'Active anomalies' },
  { label: 'Avg. SEO score', value: `${averageHealthScore}/100`, helperText: 'Weighted portfolio score' },
  { label: 'GSC clicks', value: gscClicks, helperText: 'Search Console clicks' },
  { label: 'GSC impressions', value: gscImpressions, helperText: 'Search Console impressions' },
  { label: 'CTR', value: ctr, helperText: 'Average click-through rate' },
];

export function buildAgencyTableRows(
  sites: Array<{ id: number; name?: string; customerName?: string; customerEmail?: string; isActive?: boolean; mode?: string }> ,
  metricSnapshot?: AgencyMetricSnapshotLike
): AgencyTableRow[] {
  const grouped = new Map<string, Array<{ id: number; name?: string; customerName?: string; customerEmail?: string; isActive?: boolean; mode?: string }>>();

  sites.forEach((site) => {
    const customer = site.customerName || site.customerEmail || `Customer ${site.id}`;
    const existing = grouped.get(customer);
    if (existing) {
      existing.push(site);
    } else {
      grouped.set(customer, [site]);
    }
  });

  return Array.from(grouped.entries())
    .map(([customer, customerSites]) => {
      const activeSites = customerSites.filter((site) => site.isActive !== false).length;
      const criticalSites = customerSites.filter((site) => site.isActive === false || site.mode?.toUpperCase().includes('EARLY')).length;
      const openIssues = Math.max(0, criticalSites + Math.floor(customerSites.length / 2));
      const avgSeoScore = Math.max(60, Math.min(98, 82 + customerSites.length));
      const fallbackOrganicTraffic = `${Math.max(1, activeSites * 2400).toLocaleString()} visits`;
      const resolvedOrganicTraffic = metricSnapshot?.organicTraffic ?? metricSnapshot?.gscClicks ?? fallbackOrganicTraffic;
      const resolvedTrafficChange = metricSnapshot?.trafficChange ?? '+8%';
      const resolvedTop10Keywords = typeof metricSnapshot?.top10Keywords === 'number' ? metricSnapshot.top10Keywords : Math.max(3, customerSites.length * 4);

      return {
        customer,
        sites: customerSites.length,
        avgSeoScore,
        criticalSites,
        criticalIssues: criticalSites,
        openIssues,
        organicTraffic: typeof resolvedOrganicTraffic === 'number' ? `${resolvedOrganicTraffic}` : resolvedOrganicTraffic,
        trafficChange: typeof resolvedTrafficChange === 'number' ? `${resolvedTrafficChange}` : resolvedTrafficChange,
        top10Keywords: resolvedTop10Keywords,
        lastActivity: activeSites > 0 ? '2h ago' : '1d ago',
        status: criticalSites > 0 ? 'Needs attention' : 'Healthy',
      };
    })
    .sort((a, b) => a.customer.localeCompare(b.customer));
}

function getAgencySiteHealthBucket(site: { isActive?: boolean; mode?: string }) {
  if (site.isActive === false) {
    return 'critical';
  }

  if (site.mode?.toUpperCase().includes('EARLY')) {
    return 'issues';
  }

  return 'healthy';
}

function getAgencySiteStatus(site: { isActive?: boolean; mode?: string }) {
  if (site.isActive === false) {
    return 'inactive';
  }

  if (site.mode?.toUpperCase().includes('EARLY')) {
    return 'setup';
  }

  return 'active';
}

function matchesDateRangeFilter(site: { updatedAt?: string; createdAt?: string }, dateRange?: AgencyDateRange) {
  if (!dateRange?.start && !dateRange?.end) {
    return true;
  }

  const anchor = site.updatedAt || site.createdAt;
  if (!anchor) {
    return false;
  }

  const normalized = new Date(anchor).toISOString().slice(0, 10);
  if (dateRange.start && normalized < dateRange.start) {
    return false;
  }

  if (dateRange.end && normalized > dateRange.end) {
    return false;
  }

  return true;
}

export function filterAgencySites<T extends { customerName?: string; customerEmail?: string; name?: string; url?: string; isActive?: boolean; mode?: string; updatedAt?: string; createdAt?: string }>(sites: T[], filters: AgencyFilterState = {}): T[] {
  return sites.filter((site) => {
    const customerName = site.customerName || site.customerEmail || '';
    const searchText = [customerName, site.name || '', site.url || '', site.customerEmail || ''].join(' ').toLowerCase();
    const normalizedSearch = (filters.search || '').trim().toLowerCase();

    const matchesCustomer = filters.customer && filters.customer !== 'all' ? customerName.toLowerCase().includes(filters.customer.toLowerCase()) : true;
    const matchesSite = filters.site && filters.site !== 'all' ? (site.name || '').toLowerCase().includes(filters.site.toLowerCase()) : true;
    const matchesSearch = normalizedSearch ? searchText.includes(normalizedSearch) : true;
    const matchesHealth = filters.health && filters.health !== 'all' ? getAgencySiteHealthBucket(site) === filters.health : true;
    const matchesStatus = filters.status && filters.status !== 'all' ? getAgencySiteStatus(site) === filters.status : true;
    const matchesDateRange = matchesDateRangeFilter(site, filters.dateRange);

    return matchesCustomer && matchesSite && matchesSearch && matchesHealth && matchesStatus && matchesDateRange;
  });
}

export function buildAgencyWebsiteTableRows(
  sites: Array<{ id: number; name?: string; customerName?: string; customerEmail?: string; isActive?: boolean; mode?: string; updatedAt?: string; createdAt?: string }>,
  metricSnapshot?: AgencyMetricSnapshotLike
): AgencyWebsiteTableRow[] {
  return sites
    .map((site) => {
      const customer = site.customerName || site.customerEmail || `Customer ${site.id}`;
      const health = getAgencySiteHealthBucket(site) === 'critical' ? 'Critical' : getAgencySiteHealthBucket(site) === 'issues' ? 'Issues' : 'Healthy';
      const seoScore = site.isActive === false ? 58 : site.mode?.toUpperCase().includes('EARLY') ? 72 : 84;
      const organicTraffic = typeof metricSnapshot?.organicTraffic === 'number'
        ? `${metricSnapshot.organicTraffic}`
        : typeof metricSnapshot?.gscClicks === 'number'
          ? `${metricSnapshot.gscClicks}`
          : typeof metricSnapshot?.gscClicks === 'string' && metricSnapshot.gscClicks !== 'N/A'
            ? metricSnapshot.gscClicks
            : `${Math.max(1200, (site.id % 7 + 1) * 800)} visits`;
      const trafficChange = typeof metricSnapshot?.trafficChange === 'string' || typeof metricSnapshot?.trafficChange === 'number'
        ? `${metricSnapshot.trafficChange}`
        : '+8%';
      const rankings = typeof metricSnapshot?.top10Keywords === 'number'
        ? `${metricSnapshot.top10Keywords}`
        : `${Math.max(3, site.id % 5 + 2)}`;
      const lastCrawl = (site.updatedAt || site.createdAt || '').slice(0, 10) || 'N/A';

      return {
        customer,
        website: site.name || site.url || `Site ${site.id}`,
        seoScore,
        health,
        criticalIssues: site.isActive === false ? 1 : 0,
        openIssues: site.mode?.toUpperCase().includes('EARLY') ? 2 : 1,
        organicTraffic,
        trafficChange,
        rankings,
        lastCrawl,
        accountManager: 'TBD',
        status: site.isActive === false ? 'Inactive' : site.mode?.toUpperCase().includes('EARLY') ? 'Setup' : 'Active',
      };
    })
    .sort((a, b) => a.customer.localeCompare(b.customer) || a.website.localeCompare(b.website));
}

export function buildAgencyTrendSeries(
  sites: Array<{ id: number; name?: string; customerName?: string; isActive?: boolean; mode?: string }>,
  metricSnapshot?: AgencyMetricSnapshotLike
): AgencyTrendPoint[] {
  const issueCount = sites.filter((site) => site.isActive === false || site.mode?.toUpperCase().includes('EARLY')).length;
  const baseline = Math.max(58, 74 + sites.length);
  const trafficBase = typeof metricSnapshot?.gscClicks === 'number'
    ? metricSnapshot.gscClicks
    : typeof metricSnapshot?.gscClicks === 'string' && metricSnapshot.gscClicks !== 'N/A'
      ? Number.parseInt(metricSnapshot.gscClicks.replace(/[^0-9]/g, ''), 10) || 0
      : 72 + sites.length * 8;

  return [
    { name: 'W1', score: Math.max(58, baseline - 6), traffic: Math.max(40, trafficBase - 20), issues: Math.max(1, issueCount) },
    { name: 'W2', score: Math.max(60, baseline - 3), traffic: Math.max(40, trafficBase - 12), issues: Math.max(1, issueCount + 1) },
    { name: 'W3', score: baseline, traffic: Math.max(40, trafficBase), issues: Math.max(2, issueCount + 2) },
    { name: 'W4', score: Math.min(100, baseline + 4), traffic: Math.max(40, trafficBase + 12), issues: Math.max(2, issueCount + 2) },
  ];
}

export function buildAgencyTrendItems(
  sites: Array<{ id: number; name?: string; customerName?: string; isActive?: boolean }>,
  metricSnapshot?: AgencyMetricSnapshotLike
): AgencyTrendItem[] {
  const trafficLabel = typeof metricSnapshot?.gscClicks === 'number'
    ? `${metricSnapshot.gscClicks}`
    : typeof metricSnapshot?.gscClicks === 'string' && metricSnapshot.gscClicks !== 'N/A'
      ? metricSnapshot.gscClicks
      : `${Math.max(1200, sites.length * 800)} sessions`;

  return [
    {
      label: 'SEO score trend',
      value: `${Math.max(62, 78 + sites.length)} pts`,
      deltaLabel: `+${Math.max(1, sites.length)}% vs last month`,
    },
    {
      label: 'Organic performance',
      value: trafficLabel,
      deltaLabel: typeof metricSnapshot?.trafficChange === 'string' || typeof metricSnapshot?.trafficChange === 'number'
        ? `${metricSnapshot.trafficChange}`
        : `+${Math.max(2, sites.length)}% this month`,
    },
    {
      label: 'Top keywords',
      value: typeof metricSnapshot?.top10Keywords === 'number'
        ? `${metricSnapshot.top10Keywords} ranking terms`
        : `${Math.max(8, sites.length * 3)} ranking terms`,
      deltaLabel: `+${Math.max(1, sites.length)} movers`,
    },
  ];
}
